package com.banyan.flightviewer.api

import com.banyan.flightviewer.platform.createHttpClient
import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.state.AirportFlight
import com.banyan.flightviewer.state.AirportInfo
import com.banyan.flightviewer.state.EnrichedFlightData
import com.banyan.flightviewer.state.FlightTrackPoint
import com.banyan.flightviewer.util.GeoUtils
import com.banyan.flightviewer.util.nowMs
import io.github.oshai.kotlinlogging.KotlinLogging
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.*
import kotlin.math.round
import kotlin.time.Duration.Companion.hours

private val logger = KotlinLogging.logger {}

/**
 * FlightRadar24 API client.
 *
 * API cost strategy:
 * - Periodic monitoring uses ADS-B Exchange (not FR24)
 * - Flight enrichment (on selection) uses:
 *   1. live/flight-positions/full — origin, dest, type, reg, ETA (one call per selection)
 *   2. flight-summary/light — departure time (cheapest endpoint with this data)
 * - Flight tracks are expensive and disabled by default
 */
class Fr24Client(private val apiToken: String) {

    companion object {
        private const val BASE_URL = "https://fr24api.flightradar24.com/api"
        private const val MIN_REQUEST_INTERVAL_MS = 10_000L
    }

    private val json = Json { ignoreUnknownKeys = true }

    private val client = createHttpClient {
        install(ContentNegotiation) {
            json(this@Fr24Client.json)
        }
        install(HttpTimeout) {
            requestTimeoutMillis = 15_000
        }
        defaultRequest {
            header("Authorization", "Bearer $apiToken")
            header("Accept", "application/json")
            header("Accept-Version", "v1")
        }
    }

    private var lastRequestTime = 0L

    private fun throttle() {
        val now = nowMs()
        val elapsed = now - lastRequestTime
        if (elapsed < MIN_REQUEST_INTERVAL_MS && lastRequestTime > 0) {
            logger.warn { "FR24 throttle warning: only ${elapsed}ms since last request" }
        }
        lastRequestTime = nowMs()
    }

    /**
     * Fetch aircraft within a specified radius using live/flight-positions/full.
     * Note: This is an expensive call. Prefer ADS-B Exchange for periodic monitoring.
     */
    suspend fun getAircraftInRadius(latitude: Double, longitude: Double, radiusKm: Double): List<AircraftData> {
        throttle()

        // Floor at ~9km so the viewport-derived radius at extreme zoom doesn't
        // shrink the bounding box to nothing (then the API returns no planes
        // and we also filter further with `distanceKm > radiusKm` below).
        val effectiveRadiusKm = radiusKm.coerceAtLeast(9.0)
        val bounds = GeoUtils.boundingBox(latitude, longitude, effectiveRadiusKm)
        val boundsParam = "${bounds.north},${bounds.south},${bounds.west},${bounds.east}"

        logger.debug { "FR24: fetching aircraft in bounds=$boundsParam" }

        val response = client.get("$BASE_URL/live/flight-positions/full") {
            parameter("bounds", boundsParam)
            parameter("limit", 20)
        }

        val json = response.body<JsonObject>()
        val data = json["data"]?.jsonArray ?: return emptyList()

        val aircraftList = data.mapNotNull { element ->
            val flight = element.jsonObject
            val acLat = flight["lat"]?.jsonPrimitive?.doubleOrNull ?: return@mapNotNull null
            val acLon = flight["lon"]?.jsonPrimitive?.doubleOrNull ?: return@mapNotNull null

            val distanceKm = GeoUtils.haversineDistance(latitude, longitude, acLat, acLon)
            if (distanceKm > effectiveRadiusKm) return@mapNotNull null

            val origin = flight["orig_iata"]?.jsonPrimitive?.content
                ?: flight["orig_icao"]?.jsonPrimitive?.content ?: ""
            val destination = flight["dest_iata"]?.jsonPrimitive?.content
                ?: flight["dest_icao"]?.jsonPrimitive?.content ?: ""

            AircraftData(
                hex = flight["hex"]?.jsonPrimitive?.content ?: "Unknown",
                callsign = flight["callsign"]?.jsonPrimitive?.content ?: "Unknown",
                lat = acLat,
                lon = acLon,
                altitude = flight["alt"]?.jsonPrimitive?.content ?: "0",
                speed = flight["gspeed"]?.jsonPrimitive?.doubleOrNull ?: 0.0,
                heading = flight["track"]?.jsonPrimitive?.doubleOrNull ?: 0.0,
                verticalRate = flight["vspeed"]?.jsonPrimitive?.intOrNull ?: 0,
                distance = round(distanceKm * 100.0) / 100.0,
                aircraftType = flight["type"]?.jsonPrimitive?.content ?: "Unknown",
                squawk = flight["squawk"]?.jsonPrimitive?.content ?: "",
                origin = origin,
                destination = destination,
                fr24Id = flight["fr24_id"]?.jsonPrimitive?.content ?: "",
            )
        }.sortedBy { it.distance }

        logger.info { "FR24: fetched ${aircraftList.size} aircraft within ${effectiveRadiusKm}km" }
        return aircraftList
    }

    /**
     * Bulk-fetch route data for all aircraft visible in the given bounds.
     *
     * Uses `live/flight-positions/full` with `bounds` parameter (1 API call).
     * The /light variant does NOT include origin/destination, so /full is required.
     * Returns maps keyed by both callsign and hex for flexible matching against ADSB data.
     *
     * @param callsigns ADSB callsigns (used only for logging/diagnostics)
     * @param north,south,west,east visible map bounds
     */
    /**
     * Enrich aircraft by callsign lookup. Max 15 callsigns per FR24 request.
     * Uses /full to get origin/dest fields.
     */
    suspend fun bulkEnrichByCallsigns(callsigns: List<String>): BulkEnrichResult {
        if (callsigns.isEmpty()) return BulkEnrichResult(emptyMap(), emptyMap())

        throttle()

        val batch = callsigns.take(15)
        logger.debug { "FR24 bulk enrich by callsigns: ${batch.joinToString(",")}" }

        val response = client.get("$BASE_URL/live/flight-positions/full") {
            parameter("callsigns", batch.joinToString(","))
        }

        val body = response.body<String>()
        val root = json.parseToJsonElement(body).jsonObject
        val data = root["data"]?.jsonArray
        if (data == null) {
            logger.warn { "FR24 bulk enrich: no 'data' array in response: $body" }
            return BulkEnrichResult(emptyMap(), emptyMap())
        }

        fun JsonElement.str(key: String): String? =
            jsonObject[key]?.takeIf { it !is JsonNull }?.jsonPrimitive?.content?.takeIf { it != "null" }

        val byCallsign = mutableMapOf<String, EnrichedFlightData>()
        val byHex = mutableMapOf<String, EnrichedFlightData>()

        for (element in data) {
            val origin = element.str("orig_iata") ?: element.str("orig_icao") ?: ""
            val destination = element.str("dest_iata") ?: element.str("dest_icao") ?: ""

            val enriched = EnrichedFlightData(
                origin = origin,
                destination = destination,
                aircraftType = element.str("type") ?: "",
                registration = element.str("reg") ?: "",
                fr24Id = element.str("fr24_id") ?: "",
                eta = element.str("eta") ?: "",
            )

            val callsign = element.str("callsign")?.trim()
            if (callsign != null) byCallsign[callsign] = enriched

            val hex = element.str("hex")?.lowercase()?.trim()
            if (hex != null) byHex[hex] = enriched
        }

        logger.info { "FR24 bulk enrich: ${data.size} results, ${byCallsign.size} by callsign, ${byHex.size} by hex" }
        return BulkEnrichResult(byCallsign, byHex)
    }

    @kotlinx.serialization.Serializable
    data class BulkEnrichResult(
        val byCallsign: Map<String, EnrichedFlightData>,
        val byHex: Map<String, EnrichedFlightData>,
    )

    /**
     * Fetch detailed information for a specific flight by callsign.
     * Uses live/flight-positions/full (1 credit) to get route, type, reg, ETA,
     * then flight-summary/light (cheaper) to get departure time.
     */
    suspend fun getFlightDetails(callsign: String): EnrichedFlightData? {
        throttle()

        logger.info { "FR24: fetching flight details for $callsign" }

        // Step 1: Get live position data (origin, dest, type, reg, ETA)
        val posResponse = client.get("$BASE_URL/live/flight-positions/full") {
            parameter("callsigns", callsign)
        }

        val posBody = posResponse.body<String>()
        logger.debug { "FR24 position response for $callsign: ${posBody.take(500)}" }

        val posJson = json.parseToJsonElement(posBody).jsonObject
        val posData = posJson["data"]?.jsonArray
        if (posData == null || posData.isEmpty()) {
            logger.warn { "FR24: no position data for $callsign" }
            return null
        }

        val flight = posData[0].jsonObject
        logger.debug { "FR24 flight keys: ${flight.keys}" }

        fun JsonObject.str(key: String): String? =
            this[key]?.takeIf { it !is JsonNull }?.jsonPrimitive?.content?.takeIf { it != "null" }

        val origin = flight.str("orig_iata") ?: flight.str("orig_icao") ?: ""
        val destination = flight.str("dest_iata") ?: flight.str("dest_icao") ?: ""
        val eta = flight.str("eta") ?: ""
        val fr24Id = flight.str("fr24_id") ?: ""

        // Step 2: Get departure time from flight-summary/light (cheaper than full)
        var departureTime = ""
        if (fr24Id.isNotBlank()) {
            try {
                // No throttle -- this is the second request in the same user-initiated selection flow
                val sumResponse = client.get("$BASE_URL/flight-summary/light") {
                    parameter("flight_ids", fr24Id)
                }
                val sumBody = sumResponse.body<String>()
                logger.debug { "FR24 summary response: ${sumBody.take(500)}" }

                val sumJson = json.parseToJsonElement(sumBody).jsonObject
                val sumData = sumJson["data"]?.jsonArray
                if (sumData != null && sumData.isNotEmpty()) {
                    val summary = sumData[0].jsonObject
                    departureTime = summary["datetime_takeoff"]?.jsonPrimitive?.content ?: ""
                    logger.debug { "FR24 departure time: $departureTime" }
                }
            } catch (e: Exception) {
                logger.warn { "FR24: failed to fetch summary for $fr24Id: ${e.message}" }
            }
        }

        return EnrichedFlightData(
            origin = origin,
            destination = destination,
            aircraftType = flight.str("type") ?: "",
            registration = flight.str("reg") ?: "",
            fr24Id = fr24Id,
            eta = eta,
            departureTime = departureTime,
        ).also {
            logger.info { "FR24: enriched $callsign: $origin -> $destination, eta=$eta, dep=$departureTime, fr24id=$fr24Id" }
        }
    }

    /**
     * Fetch flight track (history) for a specific flight by FR24 ID.
     * WARNING: This is an expensive API call. Use sparingly.
     */
    suspend fun getFlightTracks(fr24Id: String, limit: Int = 300): List<FlightTrackPoint> {
        if (fr24Id.isBlank()) return emptyList()

        // No throttle here -- called right after enrichment on user click
        logger.info { "FR24: fetching flight tracks for fr24Id=$fr24Id" }

        val response = client.get("$BASE_URL/flight-tracks") {
            parameter("flight_id", fr24Id)
        }

        val status = response.status.value
        val body = response.body<String>()

        if (status != 200 || body.isBlank()) {
            logger.debug { "FR24: flight-tracks status=$status for $fr24Id" }
            return emptyList()
        }
        val root = json.parseToJsonElement(body)

        // API may return an array directly or an object with a "data" array
        val flightEntries = when (root) {
            is JsonArray -> root
            is JsonObject -> root["data"]?.jsonArray ?: return emptyList()
            else -> return emptyList()
        }
        if (flightEntries.isEmpty()) return emptyList()

        val trackData = flightEntries[0].jsonObject
        val tracks = trackData["tracks"]?.jsonArray ?: return emptyList()

        return tracks.map { element ->
            val point = element.jsonObject
            // timestamp may be ISO string or unix epoch
            val ts = point["timestamp"]?.jsonPrimitive?.let { prim ->
                prim.longOrNull ?: try {
                    Instant.parse(prim.content).epochSeconds
                } catch (_: Exception) { 0L }
            } ?: 0L
            FlightTrackPoint(
                lat = point["lat"]?.jsonPrimitive?.double ?: 0.0,
                lon = point["lon"]?.jsonPrimitive?.double ?: 0.0,
                altitude = point["alt"]?.jsonPrimitive?.doubleOrNull ?: 0.0,
                timestamp = ts,
                speed = point["gspeed"]?.jsonPrimitive?.doubleOrNull ?: 0.0,
                heading = point["track"]?.jsonPrimitive?.doubleOrNull ?: 0.0,
            )
        }.also {
            logger.info { "FR24: retrieved ${it.size} track points for $fr24Id" }
        }
    }

    // ── Airport endpoints ──

    /**
     * Get full airport info (name, city, country, elevation, timezone).
     */
    suspend fun getAirportInfo(code: String): AirportInfo? {
        return try {
            val response = client.get("$BASE_URL/static/airports/$code/full")
            val body = response.body<String>()
            if (response.status.value != 200) return null
            val root = json.parseToJsonElement(body)
            val obj = when (root) {
                is JsonArray -> if (root.isNotEmpty()) root[0].jsonObject else return null
                is JsonObject -> root
                else -> return null
            }
            fun str(key: String) = obj[key]?.takeIf { it !is JsonNull }?.jsonPrimitive?.content?.takeIf { it != "null" } ?: ""
            fun int(key: String) = obj[key]?.jsonPrimitive?.intOrNull ?: 0
            fun dbl(key: String) = obj[key]?.jsonPrimitive?.doubleOrNull ?: 0.0
            val tz = obj["timezone"]?.jsonObject
            AirportInfo(
                code = str("iata"),
                icao = str("icao"),
                name = str("name"),
                city = str("city"),
                country = str("country"),
                lat = dbl("lat"),
                lon = dbl("lon"),
                elevation = int("elevation"),
                timezone = tz?.get("name")?.jsonPrimitive?.content ?: "",
                utcOffset = tz?.get("utc_offset")?.jsonPrimitive?.content ?: "",
            )
        } catch (e: Exception) {
            logger.warn { "FR24: getAirportInfo($code) failed: ${e.message}" }
            null
        }
    }

    /**
     * Get live flights departing from an airport.
     */
    suspend fun getAirportDepartures(code: String, limit: Int = 50): List<AirportFlight> {
        return getAirportFlights("outbound:$code", limit)
    }

    /**
     * Get live flights arriving at an airport.
     */
    suspend fun getAirportArrivals(code: String, limit: Int = 50): List<AirportFlight> {
        return getAirportFlights("inbound:$code", limit)
    }

    private suspend fun getAirportFlights(airportParam: String, limit: Int): List<AirportFlight> {
        return try {
            val response = client.get("$BASE_URL/live/flight-positions/full") {
                parameter("airports", airportParam)
                parameter("limit", limit)
            }
            val body = response.body<String>()
            if (response.status.value != 200) return emptyList()
            val root = json.parseToJsonElement(body).jsonObject
            val data = root["data"]?.jsonArray ?: return emptyList()

            data.mapNotNull { el ->
                fun str(key: String) = el.jsonObject[key]?.takeIf { it !is JsonNull }?.jsonPrimitive?.content?.takeIf { it != "null" } ?: ""
                val callsign = str("callsign").trim()
                if (callsign.isBlank()) return@mapNotNull null
                AirportFlight(
                    callsign = callsign,
                    flightNumber = str("flight"),
                    origin = str("orig_iata").ifEmpty { str("orig_icao") },
                    destination = str("dest_iata").ifEmpty { str("dest_icao") },
                    aircraftType = str("type"),
                    registration = str("reg"),
                    airline = str("operating_as"),
                    scheduledTime = "",
                    actualTime = str("eta"),
                    status = "en route",
                    lat = el.jsonObject["lat"]?.jsonPrimitive?.doubleOrNull ?: 0.0,
                    lon = el.jsonObject["lon"]?.jsonPrimitive?.doubleOrNull ?: 0.0,
                )
            }
        } catch (e: Exception) {
            logger.warn { "FR24: getAirportFlights($airportParam) failed: ${e.message}" }
            emptyList()
        }
    }

    /**
     * Get recently landed flights at an airport (past 2 hours via flight-summary/light).
     */
    suspend fun getRecentLandings(code: String, limit: Int = 30): List<AirportFlight> {
        return try {
            val now = Clock.System.now()
            val from = now.minus(2.hours)
            val response = client.get("$BASE_URL/flight-summary/light") {
                parameter("airports", "inbound:$code")
                parameter("flight_datetime_from", from.toString())
                parameter("flight_datetime_to", now.toString())
                parameter("sort", "desc")
                parameter("limit", limit)
            }
            val body = response.body<String>()
            if (response.status.value != 200) return emptyList()
            val root = json.parseToJsonElement(body)
            val data = when (root) {
                is JsonArray -> root
                is JsonObject -> root["data"]?.jsonArray ?: return emptyList()
                else -> return emptyList()
            }

            data.mapNotNull { el ->
                fun str(key: String) = el.jsonObject[key]?.takeIf { it !is JsonNull }?.jsonPrimitive?.content?.takeIf { it != "null" } ?: ""
                val landed = str("datetime_landed")
                if (landed.isBlank()) return@mapNotNull null // not yet landed
                AirportFlight(
                    callsign = str("callsign").trim(),
                    flightNumber = str("flight"),
                    origin = str("orig_icao"),
                    destination = str("dest_icao"),
                    aircraftType = str("type"),
                    registration = str("reg"),
                    airline = str("operating_as"),
                    scheduledTime = "",
                    actualTime = landed,
                    status = "landed",
                )
            }
        } catch (e: Exception) {
            logger.warn { "FR24: getRecentLandings($code) failed: ${e.message}" }
            emptyList()
        }
    }

    suspend fun testConnection(): Boolean {
        return try {
            getAircraftInRadius(40.6413, -73.7781, 10.0)
            true
        } catch (e: Exception) {
            logger.warn { "FR24 connection test failed: ${e.message}" }
            false
        }
    }

    fun close() {
        client.close()
    }
}
